import React, { useCallback, useState, useMemo } from 'react'
import { trackEvent } from '@fs/zion-analytics'
import { useFeatureFlags } from '@fs/zion-flags'
import { useTranslation } from 'react-i18next'
import {
  generateSls,
  saveSLSActions,
  useSlsDispatchContext,
  useSlsHighlightsContext,
  useSlsStateContext,
  getNestedOrFloatingElementById,
  getPersonName,
} from '@fs/zion-sls'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import { useR2DispatchContext, useR2StateContext } from '../../../../providers/r2Provider/R2Context'
import { errorCollector } from '../../../../../shared/utils/utils'
import ErrorOverlay from '../../../../../shared/components/errorOverlay/ErrorOverlay'
import SelectSlsField from './components/selectField/SelectSlsField'
import SelectPersons from './components/selectPersons/SelectPersons'
import ReassignHighlight from './components/reassign/ReassignHighlight'
import { R2AppName, headers } from '../../../../../shared/services/config'
import { getLinkedFields } from './components/reassign/reassignHighlightUtils'
import { getHighlightSubjectPersonId } from '../../../../utils/highlightSubject'

const SharedHighlightPanel = ({ saveToSls = true, onSave = () => {} }) => {
  const { topMostPanel, closeOverlay, editSharedHighlightPage2Overlay } = useOverlayContext()
  const r2State = useR2StateContext()
  const r2Dispatch = useR2DispatchContext()
  const { images } = useSlsStateContext()
  const slsDispatch = useSlsDispatchContext()
  const [t] = useTranslation()
  const [fieldValue, setFieldValue] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(false)
  const h = useSlsHighlightsContext()
  const {
    getHighlights,
    activeFieldInfo,
    clearEdits,
    commitEdits,
    clearDisplay,
    setActiveFieldInfo,
    setCreatingRects,
    setEditMode,
  } = h
  const {
    rip_sharedImageViewer_saveChanges: saveChangesFlag,
    rip_sharedImageViewer_sls_actions_splunk_debugger: slsActionsSplunkDebugger,
    rip_sharedImageViewer_reassignHighlightFlow: reassignHighlightFlow,
  } = useFeatureFlags([
    'rip_sharedImageViewer_saveChanges',
    'rip_sharedImageViewer_sls_actions_splunk_debugger',
    'rip_sharedImageViewer_reassignHighlightFlow',
  ])

  const updateSlsReducer = useCallback(
    (dispatches) => {
      if (dispatches?.length > 0) {
        // We need to be sure the new shared fields in state have the correct record and person ids
        const setIdsDispatches = images.reduce((acc, image) => {
          if (image.subElements)
            image.subElements.forEach((se) => acc.push({ type: 'SET_RECORD_PERSON_IDS', value: se?.id }))
          return acc
        }, [])

        slsDispatch({
          type: 'BATCH_DISPATCHES',
          value: dispatches.concat(setIdsDispatches),
        })
      }
    },
    [images, slsDispatch]
  )

  const successfulSaveCleanup = useCallback(
    (data) => {
      const { dispatches = [] } = data
      // If a new field is being created, we don't need to update the local cache because
      // those dispatches haven't been saved to the backend yet
      if (!(activeFieldInfo?.added ?? false) && saveToSls) updateSlsReducer(dispatches)
      closeOverlay('highlightsPanel')
      closeOverlay('highlightsPanelOld')
      closeOverlay('editSharedHighlightPage1')
      closeOverlay('editSharedHighlightPage2')
      closeOverlay('editSharedFieldPage1')
      closeOverlay('editSharedFieldPage2')
      r2Dispatch({
        type: 'BATCH_DISPATCHES',
        value: [
          {
            type: 'SET_EDIT_DISPATCHES',
            value: [],
          },
        ],
      })
      setActiveFieldInfo({ id: null, value: null })
      commitEdits()
      setSaving(false)

      // Call the onSave callback to allow parent components to do additional actions after save if needed
      onSave({ ...data, savedToSls: saveToSls })
    },
    [
      closeOverlay,
      r2Dispatch,
      commitEdits,
      setActiveFieldInfo,
      updateSlsReducer,
      onSave,
      activeFieldInfo?.added,
      saveToSls,
    ]
  )

  const handleSave = useCallback(
    async (data) => {
      const { dispatches = [] } = data
      const slsActions = generateSls(dispatches)
      setSaving(true)
      trackEvent({
        event_name: 'click_action',
        link_name: `Edit: Shared Field: Save`,
      })

      if (saveToSls && dispatches.length > 0 && saveChangesFlag.isOn) {
        saveSLSActions({
          actions: slsActions,
          userId: r2State.userId,
          appName: R2AppName,
          fsUserAgentChain: headers['FS-User-Agent-Chain'],
          splunkDebugModeArguments: slsActionsSplunkDebugger.isOn
            ? { theFileFromWhichSaveWasCalled: 'SharedHighlight' }
            : {},
        })
          .then(() => {
            successfulSaveCleanup(data)
          })
          .catch((err) => {
            setSaveError(err)
            errorCollector(err, null, {
              codeId: 79,
              slsActions,
              status: err?.response?.status,
              responseBody: err?.response?.data,
            })
          })
          .finally(() => {
            setSaving(false)
          })
      } else {
        successfulSaveCleanup(data)
      }
    },

    [r2State.userId, slsActionsSplunkDebugger, successfulSaveCleanup, saveChangesFlag, saveToSls]
  )

  const handleDiscard = useCallback(() => {
    closeOverlay('editSharedHighlightPage2')
    closeOverlay('editSharedHighlightPage1')
    r2Dispatch({
      type: 'SET_EDIT_DISPATCHES',
      value: [],
    })

    // Reset the state from the highlights hook
    clearEdits()
    clearDisplay({ all: true })
    setCreatingRects(false)
    setEditMode(false)
    setActiveFieldInfo({ id: null, value: null })

    closeOverlay('highlightsPanel')
    closeOverlay('highlightsPanelOld')
  }, [closeOverlay, r2Dispatch, clearEdits, clearDisplay, setCreatingRects, setEditMode, setActiveFieldInfo])

  // Logic to determine when to show ReassignHighlight vs SelectPersons
  const isFieldShareable = activeFieldInfo?.typeData?.singleSuper === false
  const linkedFields = useMemo(() => {
    return getLinkedFields(getHighlights, images, activeFieldInfo)
  }, [getHighlights, images, activeFieldInfo])
  const hasLinkedFields = linkedFields && linkedFields.length > 0

  // Check that all linked fields have the same field type as the active field
  const allFieldsSameType = useMemo(() => {
    if (!hasLinkedFields || !activeFieldInfo?.fieldType) return false

    return linkedFields.every((linkedField) => linkedField.fieldType === activeFieldInfo.fieldType)
  }, [hasLinkedFields, activeFieldInfo?.fieldType, linkedFields])

  // Show link fields via highlights panel (SelectPersons) when field is shareable and all fields same type
  // Otherwise show reassign highlight panel
  const shouldShowSelectPersons = !reassignHighlightFlow.isOn || (isFieldShareable && allFieldsSameType)
  const shouldShowReassignHighlight = reassignHighlightFlow.isOn && hasLinkedFields && !shouldShowSelectPersons

  // Prepare data for ReassignHighlight component
  const newField = useMemo(() => {
    const linkedFieldInfo = linkedFields?.[0]
    if (!linkedFieldInfo) return null

    return {
      label: linkedFieldInfo?.displayLabel || linkedFieldInfo?.label || 'New Field',
      value: linkedFieldInfo?.fieldValues?.[0]?.origValue?.text || '',
    }
  }, [linkedFields])

  const personName = useMemo(() => {
    const selectedPerson = getNestedOrFloatingElementById(images, getHighlightSubjectPersonId(r2State, activeFieldInfo))
    return selectedPerson ? getPersonName({ person: selectedPerson }) : ''
  }, [images, r2State, activeFieldInfo])

  return (
    <>
      {topMostPanel() === 'editSharedHighlightPage1' && (
        <SelectSlsField fieldValue={fieldValue} setFieldValue={setFieldValue} onDiscard={handleDiscard} />
      )}
      {topMostPanel() === 'editSharedHighlightPage2' && (
        <>
          {shouldShowSelectPersons && fieldValue && (
            <SelectPersons fieldValue={fieldValue} handleSave={handleSave} saving={saving} onDiscard={handleDiscard} />
          )}
          {shouldShowReassignHighlight && newField && fieldValue ? (
            <ReassignHighlight
              overlay={editSharedHighlightPage2Overlay}
              personName={personName}
              handleSave={handleSave}
              saving={saving}
              fieldValue={fieldValue}
              onDiscard={handleDiscard}
            />
          ) : null}
        </>
      )}
      <ErrorOverlay
        error={saveError}
        setError={setSaveError}
        title={t('save-error-overlay.heading', 'Unable to Save Changes')}
        message={t('save-error-overlay.content-generic', 'Your changes were not saved.  Please try again later.')}
      />
    </>
  )
}

export default SharedHighlightPanel
